/**
 * Bug Report Context Utilities
 * Collects game context and device information for bug reports
 */

import { UAParser } from 'ua-parser-js';

export interface GameContext {
  game: string;
  game_uuid?: string;
  level_id?: string;
  level_name?: string;
  current_score?: number;
  moves_used?: number;
  replay_session_id?: string;
  url: string;
}

export interface DeviceInfo {
  browser: string;
  os: string;
  screenResolution: string;
  viewportSize: string;
  userAgent: string;
  language: string;
  timezone: string;
}

export interface ReportData {
  version: string;
  buildTime: string;
  timestamp: string;
  browser: string;
  os: string;
  screenResolution: string;
  viewportSize: string;
  userAgent: string;
  language: string;
  timezone: string;
}

/**
 * Get game context from current URL and game state
 */
export function getGameContext(): GameContext | null {
  const path = window.location.pathname;
  const url = window.location.href;

  // Detect game from URL
  if (path.includes('/mfersbeheaded')) {
    // Extract game UUID from URL (session ID from replay or level ID)
    const replayMatch = path.match(/\/replay\/([^/]+)/);
    const levelMatch = path.match(/\/level\/([^/]+)/);
    const game_uuid = replayMatch?.[1] || levelMatch?.[1] || localStorage.getItem('mfb_session_id') || undefined;

    return {
      game: 'mfersbeheaded',
      game_uuid,
      level_id: localStorage.getItem('mfb_current_level') || undefined,
      level_name: localStorage.getItem('mfb_current_level_name') || undefined,
      current_score: parseInt(localStorage.getItem('mfb_current_score') || '0') || undefined,
      moves_used: parseInt(localStorage.getItem('mfb_moves_used') || '0') || undefined,
      replay_session_id: localStorage.getItem('mfb_replay_session_id') || undefined,
      url
    };
  }

  if (path.includes('/chess')) {
    // Extract game UUID from URL
    const gameMatch = path.match(/\/mferchess\/([^/]+)/);
    const replayMatch = path.match(/\/replay\/([^/]+)/);
    const game_uuid = replayMatch?.[1] || (gameMatch?.[1] !== 'games' && gameMatch?.[1] !== '' ? gameMatch?.[1] : undefined);

    return {
      game: 'mferchess',
      game_uuid,
      url
    };
  }

  if (path.includes('/castle')) {
    return {
      game: 'mfercastle',
      url
    };
  }

  if (path.includes('/fighting')) {
    return {
      game: 'mferfight',
      url
    };
  }

  if (path.includes('/larvalegends')) {
    return {
      game: 'larvalegends',
      url
    };
  }

  if (path.includes('/connect4')) {
    return {
      game: 'connect4',
      url
    };
  }

  if (path.includes('/tetris')) {
    return {
      game: 'mferstetris',
      url
    };
  }

  if (path.includes('/flappy')) {
    return {
      game: 'flappymfer',
      url
    };
  }

  // Not in a game, but still report the URL
  return {
    game: 'general',
    url
  };
}

/**
 * Get device and browser information
 */
export function getDeviceInfo(): DeviceInfo {
  const parser = new UAParser();
  const result = parser.getResult();

  return {
    browser: `${result.browser.name || 'Unknown'} ${result.browser.version || ''}`.trim(),
    os: `${result.os.name || 'Unknown'} ${result.os.version || ''}`.trim(),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}

/**
 * Get build information from build-info.json
 */
export async function getBuildInfo(): Promise<{ version: string; buildTime: string }> {
  try {
    const response = await fetch('/build-info.json');
    const buildInfo = await response.json();

    return {
      version: `v${buildInfo.hash.substring(0, 7)}`,
      buildTime: buildInfo.timestamp
    };
  } catch (error) {
    console.error('Failed to fetch build info:', error);
    return {
      version: 'unknown',
      buildTime: new Date().toISOString()
    };
  }
}

/**
 * Collect all report data
 */
export async function collectReportData(): Promise<ReportData> {
  const buildInfo = await getBuildInfo();
  const deviceInfo = getDeviceInfo();

  return {
    version: buildInfo.version,
    buildTime: buildInfo.buildTime,
    timestamp: new Date().toISOString(),
    ...deviceInfo
  };
}

/**
 * Resize image to fit within max file size (1MB)
 */
export async function resizeImageToFit(file: File, maxSizeBytes: number = 1024 * 1024): Promise<File> {
  // If already under size, return as is
  if (file.size <= maxSizeBytes) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate scale to reduce file size
      let scale = Math.sqrt(maxSizeBytes / file.size);
      scale = Math.min(scale, 1); // Don't upscale

      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to resize image'));
            return;
          }

          // If still too large, reduce quality
          if (blob.size > maxSizeBytes) {
            canvas.toBlob(
              (blob2) => {
                if (!blob2) {
                  reject(new Error('Failed to resize image'));
                  return;
                }
                const resizedFile = new File([blob2], file.name, {
                  type: file.type,
                  lastModified: Date.now()
                });
                resolve(resizedFile);
              },
              file.type,
              0.7 // Lower quality
            );
          } else {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          }
        },
        file.type,
        0.9 // High quality initially
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}
